// DPIA authoring (PLAN-P3 U5) — the manual/human-driven shell. Create a DPIA for a Verwerking,
// write its narrative (assembled markdown), set BIV + status. AI-assisted authoring (runDpia),
// counsel-approved publishing and PDF export are tracked separately (gate P3/P4). RLS-scoped.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, TENANT_PATHS } from "@sympllex/api-client";
import { api } from "../lib/api";
import { Can } from "../lib/rbac";
import { useI18n } from "../lib/i18n";
import { humanizeEnum } from "../lib/enum-labels";
import { PageHeader } from "../components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const errText = (e: unknown) => e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message;

export function DpiaRoute() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["dpia"] });
  const { data, isLoading, error } = useQuery({ queryKey: ["dpia"], queryFn: () => api.listDpia() });
  const rows = (data ?? []) as Array<Record<string, unknown>>;
  const [editing, setEditing] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`${t("page.dpia")} (${t("page.dpia.sub")})`}
        description="Manual authoring + document export (Print → Save as PDF). AI-assisted drafting + counsel-approved publishing are separate (pending)."
        actions={<Can perm="verwerking.write"><CreateDpiaForm onCreated={invalidate} /></Can>}
      />

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">Failed to load: {errText(error)}</p>}
      {!isLoading && !error && rows.length === 0 && <p className="text-sm text-muted-foreground">No DPIAs yet.</p>}

      {rows.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Verwerking</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Counsel review</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const id = r.id as string;
              return (
                <TableRow key={id}>
                  <TableCell className="font-medium">{String(r.verwerking ?? "—")}</TableCell>
                  <TableCell><Badge variant="secondary">{humanizeEnum(r.status)}</Badge></TableCell>
                  <TableCell>{r.triggeredCounselReview ? "Ja" : "Nee"}</TableCell>
                  <TableCell>{r.updatedAt ? new Date(String(r.updatedAt)).toLocaleDateString() : "—"}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1">
                      <Can perm="verwerking.update"><Button variant="ghost" size="sm" onClick={() => setEditing(editing === id ? null : id)}>{editing === id ? "Close" : "Open"}</Button></Can>
                      <ExportDpiaButton id={id} />
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {editing && <DpiaEditor id={editing} onDone={invalidate} onClose={() => setEditing(null)} />}
    </div>
  );
}

// DPIA document export (PLAN-P3 X6) — fetch the print-friendly HTML + open it in a new tab to print/save-as-PDF.
function ExportDpiaButton({ id }: { id: string }) {
  const dl = useMutation({
    mutationFn: async () => {
      const html = await api.exportDpia(id);
      const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  });
  return <Button variant="ghost" size="sm" onClick={() => dl.mutate()} disabled={dl.isPending}>{dl.isPending ? "…" : "Export"}</Button>;
}

function CreateDpiaForm({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [verwerkingId, setVerwerkingId] = useState("");
  const verwerkingen = (useQuery({ queryKey: ["verwerkingen"], queryFn: () => api.listVerwerkingen() }).data ?? []) as Array<Record<string, unknown>>;
  const mut = useMutation({ mutationFn: () => api.createDpia({ verwerkingId }), onSuccess: () => { setVerwerkingId(""); setOpen(false); onCreated(); } });
  if (!open) return <Button onClick={() => setOpen(true)}>+ New DPIA</Button>;
  return (
    <Card className="w-full max-w-md">
      <CardContent className="flex flex-col gap-2">
        <div className="font-semibold">New DPIA</div>
        <div className="grid gap-1.5">
          <Label htmlFor="dpia-verwerking">Verwerking *</Label>
          <Select value={verwerkingId} onValueChange={setVerwerkingId}>
            <SelectTrigger id="dpia-verwerking" className="w-full"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {verwerkingen.map((o) => <SelectItem key={String(o.id)} value={String(o.id)}>{String(o.name)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {verwerkingen.length === 0 && <p className="text-sm text-amber-800">Create a Verwerking first.</p>}
        {mut.isError && <p className="text-sm text-destructive">{errText(mut.error)}</p>}
        <div className="mt-2 flex gap-2">
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !verwerkingId}>{mut.isPending ? "Creating…" : "Create"}</Button>
          <Button variant="outline" onClick={() => { setOpen(false); mut.reset(); }}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// DPIA section scaffold — GDPR Art.35(7)(a)-(d) + residual risk & sign-off. The editor composes these
// into assembled_markdown as `## Title` blocks (and parses them back), so it round-trips with the
// document export. A raw-markdown escape hatch preserves any free-form / AI-authored narrative.
const DPIA_SECTIONS: { title: string; hint: string }[] = [
  { title: "Context & description", hint: "Art.35(7)(a) — systematic description of the processing + purposes" },
  { title: "Necessity & proportionality", hint: "Art.35(7)(b) — necessity, proportionality, lawful basis" },
  { title: "Risks to rights & freedoms", hint: "Art.35(7)(c) — risks to data subjects" },
  { title: "Measures & safeguards", hint: "Art.35(7)(d) — measures to address the risks" },
  { title: "Residual risk & DPO advice", hint: "remaining risk after measures; DPO opinion" },
  { title: "Decision & sign-off", hint: "who approved, when, conditions" },
];

/** Parse `## Title\n\nbody` blocks → { title: body }. Content before the first heading is ignored
 *  by the structured view but preserved via the raw escape hatch. */
function parseSections(md: string): Record<string, string> {
  const out: Record<string, string> = {};
  // Prepend "\n" so every "## " heading is "\n## "; body runs until the next heading or end-of-string.
  const s = "\n" + md.replace(/\r\n/g, "\n");
  const re = /\n##\s+(.+?)\n([\s\S]*?)(?=\n##\s+|$)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) out[m[1]!.trim()] = m[2]!.trim();
  return out;
}
function composeSections(values: Record<string, string>): string {
  return DPIA_SECTIONS.filter((s) => values[s.title]?.trim())
    .map((s) => `## ${s.title}\n\n${values[s.title]!.trim()}`)
    .join("\n\n");
}

// PLAN-P10 D2 — valid lifecycle actions per status, with labels.
const DPIA_ACTIONS: Record<string, { action: "submit_review" | "approve" | "request_changes" | "reject" | "reopen"; label: string; danger?: boolean }[]> = {
  authoring: [{ action: "submit_review", label: "Submit for review" }],
  authored: [{ action: "submit_review", label: "Submit for review" }],
  in_review: [{ action: "approve", label: "Approve & sign off" }, { action: "request_changes", label: "Request changes" }, { action: "reject", label: "Reject", danger: true }],
  approved: [{ action: "reopen", label: "Reopen" }],
};

function DpiaLifecycleBar({ id, row, onDone }: { id: string; row: Record<string, unknown>; onDone: () => void }) {
  const status = String(row.status ?? "authoring");
  const counsel = String(row.counselReviewStatus ?? "—");
  const effective = row.effectiveAt ? new Date(String(row.effectiveAt)).toLocaleString() : null;
  const qc = useQueryClient();
  const mut = useMutation({ mutationFn: (action: "submit_review" | "approve" | "request_changes" | "reject" | "reopen") => api.transitionDpia(id, action), onSuccess: () => { qc.invalidateQueries({ queryKey: [TENANT_PATHS.dpia, id] }); onDone(); } });
  const actions = DPIA_ACTIONS[status] ?? [];
  return (
    <Card className="bg-muted/40">
      <CardContent className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="inline-flex items-center gap-1.5">Status <Badge variant="secondary">{status}</Badge></span>
          <span className="text-muted-foreground">Counsel review: {counsel}</span>
          {effective && <span className="text-emerald-600">✓ Signed off {effective}</span>}
        </div>
        <Can perm="verwerking.write">
          <div className="flex flex-wrap gap-2">
            {actions.length === 0 && <span className="text-sm text-muted-foreground">No actions available in this status.</span>}
            {actions.map((a) => (
              <Button key={a.action} size="sm" variant={a.danger ? "destructive" : a.action === "approve" ? "default" : "outline"}
                onClick={() => mut.mutate(a.action)} disabled={mut.isPending}>
                {mut.isPending ? "…" : a.label}
              </Button>
            ))}
          </div>
        </Can>
        {mut.isError && <p className="text-sm text-destructive">{errText(mut.error)}</p>}
      </CardContent>
    </Card>
  );
}

function DpiaEditor({ id, onDone, onClose }: { id: string; onDone: () => void; onClose: () => void }) {
  const { data } = useQuery({ queryKey: [TENANT_PATHS.dpia, id], queryFn: () => api.getEntity(TENANT_PATHS.dpia, id) });
  const row = (data ?? null) as Record<string, unknown> | null;
  const [sections, setSections] = useState<Record<string, string> | null>(null);
  const [biv, setBiv] = useState<Record<string, string> | null>(null);
  const [raw, setRaw] = useState<string | null>(null); // non-null = raw mode active
  const setSection = (title: string, v: string) => setSections((s) => ({ ...(s ?? {}), [title]: v }));

  const existingMd = String(row?.assembledMarkdown ?? "");
  const sectionVals = sections ?? parseSections(existingMd);
  const bivVals = biv ?? {
    bivAvailability: String(row?.bivAvailability ?? ""), bivIntegrity: String(row?.bivIntegrity ?? ""), bivConfidentiality: String(row?.bivConfidentiality ?? ""),
  };
  const num = (v: string) => (v.trim() === "" ? null : Number(v));

  const save = useMutation({
    mutationFn: () => api.updateEntity(TENANT_PATHS.dpia, id, {
      assembledMarkdown: raw != null ? raw : composeSections(sectionVals),
      bivAvailability: num(bivVals.bivAvailability ?? ""), bivIntegrity: num(bivVals.bivIntegrity ?? ""), bivConfidentiality: num(bivVals.bivConfidentiality ?? ""),
    }),
    onSuccess: onDone,
  });
  // AI-assisted draft (D7): fetch a CNIL 4-step draft for this DPIA's verwerking → load it into raw mode
  // for the human to review + edit + save. Never auto-saved.
  const aiDraft = useMutation({
    mutationFn: () => api.draftDpiaWithAi(String(row?.verwerkingId ?? "")),
    onSuccess: (r) => { if (r.draftMarkdown) setRaw(r.draftMarkdown); },
  });
  if (!row) return null;
  const bivLabels = ["BIV — A", "BIV — I", "BIV — C"];
  return (
    <Card className="max-w-3xl">
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="font-semibold">DPIA — Art. 35(7)</div>
          <span className="inline-flex items-center gap-1">
            <Button variant="ghost" size="sm" className="text-violet-600" onClick={() => aiDraft.mutate()} disabled={aiDraft.isPending} title="Draft a CNIL 4-step DPIA with AI — you review + edit before saving">{aiDraft.isPending ? "✨ Drafting…" : "✨ AI draft"}</Button>
            <Button variant="ghost" size="sm" onClick={() => setRaw(raw != null ? null : (composeSections(sectionVals) || existingMd))}>{raw != null ? "Structured view" : "Raw markdown"}</Button>
            <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
          </span>
        </div>
        <DpiaLifecycleBar id={id} row={row} onDone={onDone} />
        <div className="flex flex-wrap gap-3">
          {(["bivAvailability", "bivIntegrity", "bivConfidentiality"] as const).map((k, i) => (
            <div key={k} className="grid min-w-24 flex-1 gap-1.5">
              <Label htmlFor={`dpia-${k}`}>{bivLabels[i]}</Label>
              <Input id={`dpia-${k}`} type="number" min={1} max={5} value={bivVals[k] ?? ""} onChange={(e) => setBiv({ ...bivVals, [k]: e.target.value })} />
            </div>
          ))}
        </div>
        {raw != null ? (
          <div className="grid gap-1.5">
            <Label htmlFor="dpia-raw">Assessment (markdown)</Label>
            <Textarea id="dpia-raw" value={raw} onChange={(e) => setRaw(e.target.value)} rows={16} className="font-mono" />
          </div>
        ) : (
          DPIA_SECTIONS.map((s) => (
            <div key={s.title} className="grid gap-1.5">
              <Label htmlFor={`dpia-sec-${s.title}`}>{s.title} <span className="text-xs font-normal text-muted-foreground">{s.hint}</span></Label>
              <Textarea id={`dpia-sec-${s.title}`} value={sectionVals[s.title] ?? ""} onChange={(e) => setSection(s.title, e.target.value)} rows={3} />
            </div>
          ))
        )}
        {save.isError && <p className="text-sm text-destructive">{errText(save.error)}</p>}
        {save.isSuccess && <p className="text-sm text-emerald-600">Saved.</p>}
        <div>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? "Saving…" : "Save"}</Button>
        </div>
      </CardContent>
    </Card>
  );
}
